/**
 * Agents page · v0.2 (2026-05-14)
 *
 * Workspace-level agent configuration UI. Mirrors prototype Section 16
 * ① "Agent 设置" panel · greek-letter avatar + model/bin/system-prompt
 * summary rows on read · expand the form drawer for create/edit.
 *
 * Backend contracts in apps/spa/src/lib/fetch-agents.ts.
 *
 * URL · `/workspace/$workspaceId/agents`
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import {
  fetchAgents,
  fetchHarnesses,
  upsertAgent,
  deleteAgent,
  avatarGlyph,
  readConfigSummary,
  harnessStatusLabel,
  isHarnessOnline,
  type AgentRow,
  type AgentUpsertBody,
  type HarnessRow,
} from '../lib/fetch-agents';
import { backendUrl } from './workspace.$workspaceId.canvas.$sessionId';
import { Icon } from '../components/Icon';

export const Route = createFileRoute('/workspace/$workspaceId/agents')({
  component: AgentsPage,
});

function readToken(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem('aiw.spa.token');
}

const AGENT_ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

type FormState = {
  mode: 'create' | 'edit';
  agentId: string;
  agentKind: AgentUpsertBody['agent_kind'];
  displayName: string;
  harnessId: string;
  model: string;
  bin: string;
  systemPrompt: string;
};

function emptyForm(): FormState {
  return {
    mode: 'create',
    agentId: '',
    agentKind: 'claude_code',
    displayName: '',
    harnessId: '',
    model: '',
    bin: '',
    systemPrompt: '',
  };
}

function formFromAgent(row: AgentRow): FormState {
  const cfg = readConfigSummary(row.config);
  return {
    mode: 'edit',
    agentId: row.agent_id,
    agentKind: row.agent_kind,
    displayName: row.display_name,
    harnessId: row.harness_id ?? '',
    model: cfg.model,
    bin: cfg.bin,
    systemPrompt: cfg.systemPrompt,
  };
}

function AgentsPage(): JSX.Element {
  const { workspaceId } = Route.useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(() => readToken());
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [harnesses, setHarnesses] = useState<HarnessRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setToken(readToken()); }, []);

  const reload = useCallback(async () => {
    if (!token) return;
    try {
      const [a, h] = await Promise.all([
        fetchAgents(backendUrl({}), token),
        fetchHarnesses(backendUrl({}), token),
      ]);
      setAgents(a);
      setHarnesses(h);
      setError(null);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    }
  }, [token]);

  useEffect(() => { void reload(); }, [reload]);

  const defaultHarnessId = useMemo(() => {
    // Prefer the first existing agent's harness, else first enrolled.
    const fromAgent = agents?.find((a) => a.harness_id)?.harness_id;
    if (fromAgent) return fromAgent;
    return harnesses?.find((h) => isHarnessOnline(h))?.harness_id ?? harnesses?.[0]?.harness_id ?? '';
  }, [agents, harnesses]);

  const harnessById = useMemo(() => {
    return new Map((harnesses ?? []).map((h) => [h.harness_id, h]));
  }, [harnesses]);

  const onlineHarnessCount = useMemo(() => {
    return (harnesses ?? []).filter((h) => isHarnessOnline(h)).length;
  }, [harnesses]);

  function startCreate(): void {
    const f = emptyForm();
    f.harnessId = defaultHarnessId;
    setForm(f);
  }

  function startEdit(row: AgentRow): void {
    setForm(formFromAgent(row));
  }

  function cancelForm(): void {
    setForm(null);
    setError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!form || !token || saving) return;
    if (!AGENT_ID_RE.test(form.agentId)) {
      setError('agent_id 格式 · 小写字母数字开头 · 仅可含 a-z 0-9 _ - · 最长 64');
      return;
    }
    if (!form.displayName.trim()) {
      setError('display_name 必填');
      return;
    }
    if (!form.harnessId.trim()) {
      setError('harness_id 必填 · 选一个已 enroll 的 harness');
      return;
    }
    const config: Record<string, unknown> = {
      version: 1,
      agent: {
        id: form.agentId,
        agent_kind: form.agentKind,
        display_name: form.displayName,
        harness_id: form.harnessId,
      },
    };
    if (form.bin.trim()) config.cli = { command: form.bin.trim() };
    if (form.model.trim()) config.model = { name: form.model.trim() };
    if (form.systemPrompt.trim()) config.system_prompt = form.systemPrompt.trim();

    setSaving(true);
    setError(null);
    try {
      await upsertAgent(backendUrl({}), token, form.agentId, {
        agent_kind: form.agentKind,
        display_name: form.displayName,
        harness_id: form.harnessId,
        config,
      });
      setForm(null);
      await reload();
    } catch (err: any) {
      setError(String(err?.message ?? err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(agentId: string): Promise<void> {
    if (!token) return;
    if (!window.confirm(`删除 agent "${agentId}" · 该 agent 当前如有 live run 会被拒。继续？`)) return;
    setError(null);
    try {
      await deleteAgent(backendUrl({}), token, agentId);
      await reload();
    } catch (err: any) {
      setError(String(err?.message ?? err));
    }
  }

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <button
          type="button"
          onClick={() => navigate({ to: '/workspace/$workspaceId', params: { workspaceId } })}
          style={backBtnStyle}
          data-testid="agents-back-to-hub"
        >
          <span style={buttonContentStyle}>
            <Icon name="chev-left" size={14} />
            工作区
          </span>
        </button>
        <h1 style={titleStyle}>Agent 设置</h1>
        <code style={codeStyle}>{workspaceId}</code>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={startCreate}
          disabled={!token || form !== null}
          style={primaryBtnStyle(!token || form !== null)}
          data-testid="agents-new"
        >
          <span style={buttonContentStyle}>
            <Icon name="plus" size={15} />
            新建 agent
          </span>
        </button>
      </div>

      <div style={metaStyle}>
        {!token
          ? '未登录 · 请先到 /login'
          : agents === null
            ? '加载中…'
            : agents.length === 0
              ? '当前 workspace 没有 agent · 点右上角"新建 agent"开始'
              : `共 ${agents.length} 个 agent · ${onlineHarnessCount}/${harnesses?.length ?? 0} 个 harness 在线`}
      </div>

      {error && (
        <div style={errorStyle} data-testid="agents-error">{error}</div>
      )}

      {form && (
        <AgentForm
          form={form}
          setForm={setForm}
          harnesses={harnesses ?? []}
          saving={saving}
          onSubmit={handleSubmit}
          onCancel={cancelForm}
        />
      )}

      {agents !== null && agents.length > 0 && (
        <div style={gridStyle}>
          {agents.map((a) => {
            const cfg = readConfigSummary(a.config);
            const boundHarness = a.harness_id ? harnessById.get(a.harness_id) : undefined;
            return (
              <div key={a.agent_id} style={cardStyle} data-testid={`agents-card-${a.agent_id}`}>
                <div style={cardHeadStyle}>
                  <div style={avatarStyle(a.agent_kind)}>{avatarGlyph(a.agent_id)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={cardTitleStyle}>{a.display_name}</div>
                    <div style={cardSubStyle}>
                      <code>{a.agent_id}</code> · {a.agent_kind}
                    </div>
                  </div>
                  <span style={kindChipStyle(a.agent_kind)}>{a.agent_kind}</span>
                </div>
                <div style={cardBodyStyle}>
                  <Row label="harness">
                    {a.harness_id
                      ? (
                        <span>
                          {a.harness_id}
                          <span style={harnessStatusChipStyle(boundHarness)}>{harnessStatusLabel(boundHarness)}</span>
                        </span>
                      )
                      : <em>未绑定</em>}
                  </Row>
                  <Row label="model">{cfg.model || <em>(默认)</em>}</Row>
                  <Row label="binary">{cfg.bin || <em>(PATH 解析)</em>}</Row>
                  <Row label="system_prompt">
                    {cfg.systemPrompt
                      ? <span style={promptPreviewStyle}>{cfg.systemPrompt.slice(0, 140)}{cfg.systemPrompt.length > 140 ? '…' : ''}</span>
                      : <em>(无)</em>}
                  </Row>
                </div>
                <div style={cardFootStyle}>
                  <button
                    type="button"
                    onClick={() => startEdit(a)}
                    style={secondaryBtnStyle()}
                    data-testid={`agents-edit-${a.agent_id}`}
                  >
                    编辑
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(a.agent_id)}
                    style={dangerBtnStyle()}
                    data-testid={`agents-delete-${a.agent_id}`}
                  >
                    删除
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface AgentFormProps {
  form: FormState;
  setForm: (f: FormState) => void;
  harnesses: HarnessRow[];
  saving: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
}

function AgentForm(props: AgentFormProps): JSX.Element {
  const { form, setForm, harnesses, saving, onSubmit, onCancel } = props;
  const upd = (patch: Partial<FormState>): void => setForm({ ...form, ...patch });

  return (
    <form onSubmit={onSubmit} style={formStyle} data-testid="agents-form">
      <div style={formHeadStyle}>
        <strong>{form.mode === 'create' ? '新建 agent' : `编辑 · ${form.agentId}`}</strong>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onCancel} style={ghostBtnStyle()} data-testid="agents-form-cancel">取消</button>
      </div>

      <Field label="agent_id" hint="小写字母数字开头 · 仅可含 a-z 0-9 _ - · 最长 64">
        <input
          type="text"
          value={form.agentId}
          onChange={(e) => upd({ agentId: e.target.value })}
          disabled={form.mode === 'edit' || saving}
          placeholder="例 · alpha · beta · review-alpha"
          style={inputStyle}
          data-testid="agents-form-id"
          required
        />
      </Field>

      <Field label="agent_kind">
        <select
          value={form.agentKind}
          onChange={(e) => upd({ agentKind: e.target.value as FormState['agentKind'] })}
          disabled={saving}
          style={inputStyle}
          data-testid="agents-form-kind"
        >
          <option value="claude_code">claude_code (本机 claude CLI)</option>
          <option value="codex">codex (本机 codex CLI · adapter Phase B 内)</option>
          <option value="api_only">api_only</option>
          <option value="custom">custom</option>
        </select>
      </Field>

      <Field label="display_name">
        <input
          type="text"
          value={form.displayName}
          onChange={(e) => upd({ displayName: e.target.value })}
          disabled={saving}
          placeholder="给同事看的友好名 · 例 · Claude α 主力"
          style={inputStyle}
          data-testid="agents-form-display-name"
          required
        />
      </Field>

      <Field label="harness_id" hint="把这个 agent 绑定到本 workspace 已 enroll 的某台 harness">
        <select
          value={form.harnessId}
          onChange={(e) => upd({ harnessId: e.target.value })}
          disabled={saving}
          style={inputStyle}
          data-testid="agents-form-harness-id"
          required
        >
          <option value="" disabled>选一个 harness…</option>
          {harnesses.map((h) => (
            <option key={h.harness_id} value={h.harness_id}>
              {h.harness_id} · {h.display_name} · {harnessStatusLabel(h)}
            </option>
          ))}
        </select>
      </Field>

      <Field label="model" hint="可选 · claude-sonnet-4-6 / claude-opus-4-7 · 默认随 harness orchestrator 配置">
        <input
          type="text"
          value={form.model}
          onChange={(e) => upd({ model: e.target.value })}
          disabled={saving}
          placeholder="claude-sonnet-4-6"
          style={inputStyle}
          data-testid="agents-form-model"
        />
      </Field>

      <Field label="binary 路径" hint="可选 · 不填走 PATH 解析 · 例 · /Users/you/.local/bin/claude">
        <input
          type="text"
          value={form.bin}
          onChange={(e) => upd({ bin: e.target.value })}
          disabled={saving}
          placeholder="/path/to/claude"
          style={inputStyle}
          data-testid="agents-form-bin"
        />
      </Field>

      <Field label="system_prompt" hint="可选 · agent 角色指引 · 留空走默认（含画布拆解规则）">
        <textarea
          value={form.systemPrompt}
          onChange={(e) => upd({ systemPrompt: e.target.value })}
          disabled={saving}
          rows={6}
          placeholder="你是 α · 这个会话的执行体…"
          style={{ ...inputStyle, fontFamily: 'var(--font-mono)', resize: 'vertical' }}
          data-testid="agents-form-system-prompt"
        />
      </Field>

      <div style={formFootStyle}>
        <button
          type="submit"
          disabled={saving}
          style={primaryBtnStyle(saving)}
          data-testid="agents-form-submit"
        >
          {saving ? '保存中…' : form.mode === 'create' ? '创建' : '保存'}
        </button>
      </div>
    </form>
  );
}

function Field(props: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={fieldStyle}>
      <div style={fieldLabelStyle}>
        <span style={{ fontWeight: 600 }}>{props.label}</span>
        {props.hint && <span style={fieldHintStyle}>· {props.hint}</span>}
      </div>
      {props.children}
    </label>
  );
}

function Row(props: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={rowStyle}>
      <span style={rowLabelStyle}>{props.label}</span>
      <span style={rowValueStyle}>{props.children}</span>
    </div>
  );
}

// === Styles · matches workspace.$workspaceId.tsx ===

const pageStyle: CSSProperties = {
  maxWidth: 1100,
  margin: '0 auto',
  padding: 'var(--space-8) var(--space-6)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-8)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 'var(--space-6)',
  flexWrap: 'wrap',
};

const backBtnStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  padding: '4px 10px',
  border: '1.5px solid var(--border-soft)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  cursor: 'pointer',
};

const buttonContentStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
};

const titleStyle: CSSProperties = {
  fontFamily: 'var(--font-title)',
  fontSize: 32,
  fontWeight: 700,
  color: 'var(--fg-default)',
  margin: 0,
};

const codeStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  color: 'var(--fg-soft)',
  background: 'var(--bg-muted)',
  padding: '2px 8px',
  borderRadius: 'var(--radius-xs)',
};

const metaStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--fg-soft)',
};

const errorStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  color: 'var(--accent-danger)',
  background: 'var(--bg-muted)',
  border: '1px solid var(--accent-danger)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 12px',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: 'var(--space-8)',
};

const cardStyle: CSSProperties = {
  background: 'var(--bg-surface)',
  border: '2px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  transform: 'rotate(-0.15deg)',
};

const cardHeadStyle: CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1.5px dashed var(--border-soft)',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: 'var(--bg-subtle)',
};

function avatarStyle(kind: AgentRow['agent_kind']): CSSProperties {
  const color =
    kind === 'claude_code' ? 'var(--accent-primary)' :
    kind === 'codex' ? 'var(--accent-success)' :
    kind === 'api_only' ? 'var(--accent-warning)' :
    'var(--fg-soft)';
  return {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: color,
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    fontFamily: 'var(--font-mono)',
    fontSize: 16,
    fontWeight: 700,
    flexShrink: 0,
  };
}

const cardTitleStyle: CSSProperties = {
  fontFamily: 'var(--font-title)',
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--fg-default)',
};

const cardSubStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  color: 'var(--fg-soft)',
  marginTop: 2,
};

function kindChipStyle(kind: AgentRow['agent_kind']): CSSProperties {
  const colorMap = {
    claude_code: { bg: 'var(--accent-info-bg, #e0f2fe)', fg: 'var(--accent-primary)' },
    codex: { bg: 'var(--accent-success-bg, #d1fae5)', fg: 'var(--accent-success)' },
    api_only: { bg: 'var(--bg-muted)', fg: 'var(--accent-warning)' },
    custom: { bg: 'var(--bg-muted)', fg: 'var(--fg-soft)' },
  } as const;
  const c = colorMap[kind] ?? colorMap.custom;
  return {
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 'var(--radius-pill)',
    background: c.bg,
    color: c.fg,
    textTransform: 'lowercase',
  };
}

function harnessStatusChipStyle(row: HarnessRow | undefined): CSSProperties {
  const online = isHarnessOnline(row);
  return {
    marginLeft: 6,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 6px',
    borderRadius: 'var(--radius-pill)',
    fontSize: 10,
    fontFamily: 'var(--font-mono)',
    background: online ? 'var(--accent-success-bg, #d1fae5)' : 'var(--bg-muted)',
    color: online ? 'var(--accent-success)' : 'var(--fg-soft)',
  };
}

const cardBodyStyle: CSSProperties = {
  padding: '10px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
};

const rowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '88px 1fr',
  gap: 8,
  alignItems: 'baseline',
};

const rowLabelStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  color: 'var(--fg-soft)',
};

const rowValueStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11.5,
  color: 'var(--fg-default)',
  wordBreak: 'break-all',
};

const promptPreviewStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  color: 'var(--fg-soft)',
  lineHeight: 1.4,
};

const cardFootStyle: CSSProperties = {
  padding: '8px 12px',
  borderTop: '1.5px dashed var(--border-soft)',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
};

const formStyle: CSSProperties = {
  background: 'var(--bg-surface)',
  border: '2px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
  padding: 'var(--space-6)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const formHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: 'var(--font-title)',
  fontSize: 16,
  borderBottom: '1px solid var(--border-soft)',
  paddingBottom: 8,
  marginBottom: 4,
};

const fieldStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const fieldLabelStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  color: 'var(--fg-default)',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
};

const fieldHintStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 11,
  color: 'var(--fg-soft)',
};

const inputStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  padding: '6px 10px',
  border: '1.5px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};

const formFootStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 4,
};

function primaryBtnStyle(disabled: boolean): CSSProperties {
  return {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    fontWeight: 600,
    padding: '6px 14px',
    border: '2px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)',
    background: disabled ? 'var(--bg-muted)' : 'var(--accent-primary)',
    color: disabled ? 'var(--fg-faint)' : 'var(--accent-primary-fg)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : 'var(--shadow-card)',
  };
}

function secondaryBtnStyle(): CSSProperties {
  return {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    padding: '4px 10px',
    border: '1.5px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-surface)',
    cursor: 'pointer',
  };
}

function ghostBtnStyle(): CSSProperties {
  return {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    padding: '4px 10px',
    border: '1.5px solid var(--border-soft)',
    borderRadius: 'var(--radius-sm)',
    background: 'transparent',
    color: 'var(--fg-soft)',
    cursor: 'pointer',
  };
}

function dangerBtnStyle(): CSSProperties {
  return {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    padding: '4px 10px',
    border: '1.5px solid var(--accent-danger)',
    borderRadius: 'var(--radius-sm)',
    background: 'transparent',
    color: 'var(--accent-danger)',
    cursor: 'pointer',
  };
}

export default AgentsPage;
